import type {
  NominationSaveStep1,
  NominationSaveStep2,
  NominationSaveStep3,
  NominationSaveStep4,
  NominationSaveStep4File,
  NominationStepsResponse,
  SaveNominationStepsPayload,
} from '../types/nominationApi';
import type { NominationWizardDraft } from '../types/nominationCaseRequest';
import {
  draftHasOtherDeliverySelected,
} from '../utils/deliveryMethod';
import { isDocumentTypeUuid, isPredefinedDocumentType, resolveDocumentTypeUuid } from '../utils/documentTypeId';
import { caseRequestIdForSave, draftResumeStepFromApi, finalizeCaseRequestSavePayload, SaveStepFields, strOrNull } from '../utils/caseRequestPayload';
import { looksLikeStorageKey, normalizeDocumentFileFields } from '../utils/fileDisplayName';
import type { NominationWizardStep } from '../pages/wizard/nomination/nominationConstants';
import {
  FILE_CATEGORY_NOMINATION,
  NOMINATION_CATEGORY_ID,
} from '../pages/wizard/nomination/nominationConstants';
import {
  parseApplicableAreasFromApi,
  parsePhysicalFileDeliveryMethods,
} from './criminalCaseMapper';

export type { NominationWizardStep };

export function caseRequestIdFromResponse(response: NominationStepsResponse): string {
  const id = response.case_request_id ?? response.caseRequestId;
  return typeof id === 'string' ? id.trim() : '';
}

export function referenceNoFromResponse(response: NominationStepsResponse): string | undefined {
  const ref = response.reference_no ?? response.referenceNo ?? response.fileReferenceNumber;
  const t = typeof ref === 'string' ? ref.trim() : '';
  return t || undefined;
}

function stepProcessed(
  response: NominationStepsResponse,
  camel: keyof NominationStepsResponse,
  snake: keyof NominationStepsResponse,
): boolean | undefined {
  const c = response[camel];
  if (typeof c === 'boolean') return c;
  const s = response[snake];
  if (typeof s === 'boolean') return s;
  return undefined;
}

function step1LooksComplete(s1: NominationSaveStep1 | undefined, response: NominationStepsResponse): boolean {
  const subId = (s1?.sub_category_id ?? response.file_sub_category_id ?? '').trim();
  const desc = (s1?.description ?? '').trim();
  return Boolean(subId && desc);
}

function step2LooksComplete(s2: NominationSaveStep2 | undefined): boolean {
  return Boolean((s2?.urgency_level_id ?? '').trim());
}

function step3LooksComplete(s3: NominationSaveStep3 | undefined): boolean {
  return Boolean((s3?.physical_file_delivery_method_ids ?? []).filter(Boolean).length);
}

function step4LooksComplete(s4: NominationSaveStep4 | undefined): boolean {
  const files = step4DocumentsFromApi(s4);
  return files.some(
    (f) =>
      Boolean((f.file_name ?? '').trim() || (f.original_file_name ?? '').trim()) ||
      Boolean((f.reason ?? '').trim()),
  );
}

export function resolveNominationDraftResumeStep(response: NominationStepsResponse): NominationWizardStep {
  return draftResumeStepFromApi(
    response,
    () => {
  if (stepProcessed(response, 'step1Processed', 'step_1_processed') === false) return 1;
  if (stepProcessed(response, 'step2Processed', 'step_2_processed') === false) return 2;
  if (stepProcessed(response, 'step3Processed', 'step_3_processed') === false) return 3;
  if (stepProcessed(response, 'step4Processed', 'step_4_processed') === false) return 4;

  const s1 = response.step_1;
  const s2 = response.step_2;
  const s3 = response.step_3;
  const s4 = response.step_4;

  if (!step1LooksComplete(s1, response)) return 1;
  if (!step2LooksComplete(s2)) return 2;
  if (!step3LooksComplete(s3)) return 3;
  if (!step4LooksComplete(s4)) return 4;
  return 4;
    },
    (step) => step >= 1 && step <= 4,
  );
}

function step4DocumentsFromApi(s4: NominationSaveStep4 | undefined): NominationSaveStep4File[] {
  return [
    ...(s4?.uploaded_files ?? []),
    ...(s4?.extra_uploaded_files ?? []),
    ...(s4?.documents ?? []),
  ];
}

function apiDocumentTypeId(file: NominationSaveStep4File): string {
  return (file.document_type_id ?? file.document_id ?? '').trim();
}

function yesNoToBool(v: '' | 'yes' | 'no'): boolean | undefined {
  if (v === 'yes') return true;
  if (v === 'no') return false;
  return undefined;
}

function boolToYesNo(v: boolean | undefined | null): '' | 'yes' | 'no' {
  if (v === true) return 'yes';
  if (v === false) return 'no';
  return '';
}

type SavedFileRow = {
  file_name?: string | null;
  original_file_name?: string | null;
  fileName?: string | null;
  originalFileName?: string | null;
};

function savedFileFields(file: SavedFileRow): { fileName: string; originalFileName: string } {
  const apiFileName = file.file_name ?? file.fileName ?? '';
  const original = file.original_file_name ?? file.originalFileName;
  return normalizeDocumentFileFields(apiFileName, original);
}

function step4FileFields(file: NominationSaveStep4File): { fileName: string; originalFileName: string } {
  return savedFileFields(file);
}

function step4FileNamesForSave(
  fileName: string | null | undefined,
  originalFileName: string | null | undefined,
): { file_name?: string; original_file_name?: string } {
  const a = fileName?.trim() ?? '';
  const b = originalFileName?.trim() ?? '';
  if (!a && !b) return {};

  if (a && looksLikeStorageKey(a)) {
    return { file_name: a, ...(b ? { original_file_name: b } : {}) };
  }
  if (b && looksLikeStorageKey(b)) {
    return { file_name: b, ...(a ? { original_file_name: a } : {}) };
  }

  return { file_name: a, ...(b ? { original_file_name: b } : {}) };
}

function otherMethodFromStep3(s3: NominationSaveStep3 | undefined): string {
  const raw = s3?.other_method;
  return typeof raw === 'string' ? raw.trim() : '';
}

function otherMethodFromDeliveryRows(s3: NominationSaveStep3 | undefined): string {
  const raw = s3?.physical_file_delivery_methods;
  if (!raw?.length || typeof raw[0] === 'string') return '';

  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const name = item.delivery_method_name?.trim() ?? '';
    const other = item.other_method?.trim();
    if (name.toLowerCase() === 'other' && other) return other;
  }
  return '';
}

function resolveApplicableAreaIds(response: NominationStepsResponse, prev: string[]): string[] {
  for (const src of [response.applicable_areas]) {
    const { ids } = parseApplicableAreasFromApi(src);
    if (ids.length) return ids;
  }
  if (Array.isArray(response.applicableAreaIds) && response.applicableAreaIds.length) {
    return response.applicableAreaIds.filter((id) => typeof id === 'string' && id.trim());
  }
  return prev;
}

function resolveApplicableAreaNames(response: NominationStepsResponse, prev: string[]): string[] {
  for (const src of [response.applicable_areas]) {
    const { names } = parseApplicableAreasFromApi(src);
    if (names.length) return names;
  }
  const named = response.applicable_area_names ?? response.applicableAreaNames;
  if (Array.isArray(named) && named.length) {
    return named.filter((n): n is string => typeof n === 'string' && Boolean(n.trim()));
  }
  return prev;
}

function mapStep1(draft: NominationWizardDraft): SaveStepFields<NominationSaveStep1> {
  return {
    description: strOrNull(draft.description),
    sub_category_id: strOrNull(draft.subFileCategoryId),
    case_number: strOrNull(draft.caseNumber),
    has_connected_case: yesNoToBool(draft.hasConnectedMatters),
    connected_case_number:
      draft.hasConnectedMatters === 'yes' ? strOrNull(draft.connectedCaseNumber) : undefined,
    connected_agd_file_ref_number:
      draft.hasConnectedMatters === 'yes' ? strOrNull(draft.connectedAgdFileRef) : undefined,
  };
}

function mapStep2(draft: NominationWizardDraft): SaveStepFields<NominationSaveStep2> {
  return {
    urgency_level_id: strOrNull(draft.urgencyLevelId),
    deadline: strOrNull(draft.deadline),
    urgency_justification: strOrNull(draft.urgencyJustification),
  };
}

function mapStep3(draft: NominationWizardDraft): SaveStepFields<NominationSaveStep3> {
  const ids = draft.deliveryMethodIds.filter(Boolean);
  const step3: NominationSaveStep3 = {
    physical_file_delivery_method_ids: ids.length ? ids : undefined,
  };

  if (draftHasOtherDeliverySelected(draft)) {
    step3.other_method = strOrNull(draft.deliveryOther);
  }

  return step3;
}

function mapStep4(draft: NominationWizardDraft): SaveStepFields<NominationSaveStep4> {
  const uploaded_files = draft.documents
    .filter(
      (d) =>
        isPredefinedDocumentType(d.documentTypeId, d.checklistItemId) && (strOrNull(d.fileName) || strOrNull(d.skipReason)),
    )
    .map((d) => {
      const reason = strOrNull(d.skipReason);
      const names = step4FileNamesForSave(strOrNull(d.fileName), strOrNull(d.originalFileName));
      const documentTypeId = resolveDocumentTypeUuid(d.documentTypeId, d.checklistItemId);
      return {
        ...(documentTypeId ? { document_type_id: documentTypeId } : {}),
        ...names,
        ...(reason ? { reason } : {}),
      };
    });
  const extra_uploaded_files = draft.documents
    .filter(
      (d) =>
        !isPredefinedDocumentType(d.documentTypeId, d.checklistItemId) && (strOrNull(d.fileName) || strOrNull(d.skipReason)),
    )
    .map((d) => {
      const reason = strOrNull(d.skipReason);
      const names = step4FileNamesForSave(strOrNull(d.fileName), strOrNull(d.originalFileName));
      return {
        ...names,
        ...(reason ? { reason } : {}),
      };
    });

  return {
    ...(uploaded_files.length ? { uploaded_files } : {}),
    ...(extra_uploaded_files.length ? { extra_uploaded_files } : {}),
  };
}

export function buildSaveNominationPayload(
  draft: NominationWizardDraft,
  options: {
    currentStep?: NominationWizardStep;
    isDraft: boolean;
    confirmedCorrect?: boolean;
    allSteps?: boolean;
    isUpdateAlreadySubmitted?: boolean;
  },
): SaveNominationStepsPayload {
  const areaIds = draft.applicableAreaIds.filter(Boolean);
  const payload: SaveNominationStepsPayload = {
    is_draft_status: options.isDraft,
  };
  const caseRequestId = caseRequestIdForSave(draft.draftRequestId);
  if (caseRequestId) payload.case_request_id = caseRequestId;

  if (options.isUpdateAlreadySubmitted) payload.is_update_already_submitted = true;
  if (options.confirmedCorrect != null) payload.confirmed_correct = options.confirmedCorrect;
  if (caseRequestId || areaIds.length) payload.applicable_areas = areaIds.length ? areaIds : null;

  if (options.allSteps) {
    payload.step_1 = mapStep1(draft);
    payload.step_2 = mapStep2(draft);
    payload.step_3 = mapStep3(draft);
    payload.step_4 = mapStep4(draft);
    return finalizeCaseRequestSavePayload(payload, options);
  }

  const step = options.currentStep;
  if (!step) return finalizeCaseRequestSavePayload(payload, options);

  if (options.isDraft) {
    if (step >= 1) payload.step_1 = mapStep1(draft);
    if (step >= 2) payload.step_2 = mapStep2(draft);
    if (step >= 3) payload.step_3 = mapStep3(draft);
    if (step >= 4) payload.step_4 = mapStep4(draft);
    return finalizeCaseRequestSavePayload(payload, options);
  }

  if (step === 1) payload.step_1 = mapStep1(draft);
  if (step === 2) payload.step_2 = mapStep2(draft);
  if (step === 3) payload.step_3 = mapStep3(draft);
  if (step === 4) payload.step_4 = mapStep4(draft);

  return finalizeCaseRequestSavePayload(payload, options);
}

export function mapNominationStepsToDraft(
  response: NominationStepsResponse,
  prev: NominationWizardDraft,
): NominationWizardDraft {
  const s1 = response.step_1;
  const s2 = response.step_2;
  const s3 = response.step_3;
  const s4 = response.step_4;

  const documents = (() => {
    const fromApi = step4DocumentsFromApi(s4);
    if (!fromApi.length) return prev.documents;
    if (!prev.documents.length) {
      return fromApi
        .filter((f) => {
          const typeId = apiDocumentTypeId(f);
          return typeId
            ? isDocumentTypeUuid(typeId)
            : Boolean((f.file_name ?? '').trim() || (f.reason ?? '').trim());
        })
        .map((f) => {
          const { fileName, originalFileName } = step4FileFields(f);
          const typeId = apiDocumentTypeId(f);
          return {
            id: (f.case_request_document_id ?? '').trim() || crypto.randomUUID(),
            checklistItemId: '',
            documentTypeId: typeId,
            documentName: 'Document',
            mandatory: false,
            fileName,
            originalFileName,
            uploadTime: f.upload_time ?? '',
            skipReason: f.reason ?? '',
          };
        });
    }
    return prev.documents.map((doc) => {
      const match = fromApi.find((f) => {
        const typeId = apiDocumentTypeId(f);
        if (typeId && typeId === doc.documentTypeId) return true;
        return false;
      });
      if (!match) return doc;
      const { fileName, originalFileName } = step4FileFields(match);
      return {
        ...doc,
        documentTypeId: apiDocumentTypeId(match) || doc.documentTypeId,
        fileName: fileName || doc.fileName,
        originalFileName: originalFileName || doc.originalFileName,
        uploadTime: match.upload_time ?? doc.uploadTime,
        skipReason: match.reason ?? doc.skipReason,
      };
    });
  })();

  const delivery = parsePhysicalFileDeliveryMethods(s3?.physical_file_delivery_methods);
  const deliveryIds = s3?.physical_file_delivery_method_ids?.length
    ? s3.physical_file_delivery_method_ids
    : delivery.ids;

  const fileCategoryId =
    (response.file_category_id ?? s1?.category_id ?? prev.fileCategoryId) || NOMINATION_CATEGORY_ID;
  const fileCategoryName =
    (response.file_category_name ?? s1?.category_name ?? prev.fileCategory) || FILE_CATEGORY_NOMINATION;
  const subFileCategoryId =
    response.file_sub_category_id ?? s1?.sub_category_id ?? prev.subFileCategoryId;
  const subFileCategoryName =
    response.file_sub_category_name ?? s1?.sub_category_name ?? prev.subFileCategoryName;

  return {
    ...prev,
    draftRequestId: caseRequestIdFromResponse(response) || prev.draftRequestId,
    referenceNo: referenceNoFromResponse(response) ?? prev.referenceNo,
    fileCategory: fileCategoryName,
    fileCategoryId,
    subFileCategoryId,
    subFileCategoryName,
    applicableAreaIds: resolveApplicableAreaIds(response, prev.applicableAreaIds),
    applicableAreas: resolveApplicableAreaNames(response, prev.applicableAreas),
    description: s1?.description ?? prev.description,
    caseNumber: s1?.case_number ?? prev.caseNumber,
    hasConnectedMatters: boolToYesNo(s1?.has_connected_case) || prev.hasConnectedMatters || 'no',
    connectedCaseNumber: s1?.connected_case_number ?? prev.connectedCaseNumber,
    connectedAgdFileRef: s1?.connected_agd_file_ref_number ?? prev.connectedAgdFileRef,
    urgencyLevelId: s2?.urgency_level_id ?? prev.urgencyLevelId,
    urgencyLevelName: s2?.urgency_level_name ?? prev.urgencyLevelName,
    deadline: s2?.deadline ?? prev.deadline,
    urgencyJustification: s2?.urgency_justification ?? prev.urgencyJustification,
    deliveryMethodIds: deliveryIds.length ? deliveryIds : prev.deliveryMethodIds,
    deliveryMethods: delivery.names.length ? delivery.names : prev.deliveryMethods,
    deliveryOther: otherMethodFromStep3(s3) || otherMethodFromDeliveryRows(s3) || prev.deliveryOther,
    documents,
  };
}
